import { Form } from '@homex/se-react-components';
import styled, { css } from 'styled-components/macro';

import { ReactComponent as ChevronRightSvg } from 'components/svg/ChevronRight.svg';
import { ReactComponent as SelectServiceTypeMaintenanceSvg } from 'components/svg/SelectServiceTypeMaintenance.svg';
import { ReactComponent as SelectServiceTypeQuoteSvg } from 'components/svg/SelectServiceTypeQuote.svg';
import { ReactComponent as SelectServiceTypeQuoteSmallSvg } from 'components/svg/SelectServiceTypeQuoteSmall.svg';
import { ReactComponent as SelectServiceTypeRepairSvg } from 'components/svg/SelectServiceTypeRepair.svg';
import { large, medium } from 'styles/breakpoints';
import { container } from 'styles/layout';
import { eyebrow, heading, paragraph } from 'styles/typography';
import { IWithThemeProps } from 'typings';

interface IButtonGroupProps {
  serviceTypeLength: number;
}

interface ILabelProps {
  serviceTypeLength: number;
}

export const Heading = styled.h1`
  ${heading}

  margin-bottom: 22px;
  margin-top: 24px;

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}

  ${large`
    margin-bottom: 18px;
  `}
`;

export const ButtonGroup = styled(Form)<IButtonGroupProps>`
  ${container}

  align-items: stretch;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 auto 38px;
  position: relative;

  ${large`
    flex-direction: row;
    margin: 0;
    width: auto;
  `}

  ${({ serviceTypeLength }) => {
    if (serviceTypeLength === 2) {
      return large`
        margin: 0 40px;
      `;
    }

    return '';
  }}
`;

export const Label = styled.label<ILabelProps>`
  align-items: center;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  justify-content: space-between;
  overflow: hidden;
  padding: 25px 8px;
  user-drag: none;
  user-select: none;

  ${large`
    flex-direction: column;
    padding: 28px 22px 33px;
  `}

  ${({ serviceTypeLength }) => {
    if (serviceTypeLength === 2) {
      return large`
        padding: 28px 52px 33px;
      `;
    }

    return '';
  }}
`;

export const Chevron = styled(ChevronRightSvg)`
  flex: 1 0 auto;
  height: 21px;
  margin-left: 13px;
  width: 11px;

  ${medium`
      margin-left: 15px;
  `}

  ${large`
      display: none;
  `}
`;

export const ButtonHeader = styled.h2`
  ${eyebrow}

  margin: 0 0 5px;
  text-align: left;

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}

  ${large`
    margin: 0 0 7px;
    text-align: center;
  `}
`;

export const ButtonDetails = styled.p`
  ${paragraph}

  font: 14px/18px proxima-nova, sans-serif;
  margin: 0;
  text-align: left;

  ${({ theme }: IWithThemeProps) => `
    color: ${theme.topForegroundPrimary};
  `}

  ${large`
    text-align: center;
  `}
`;

export const ButtonText = styled.div`
  width: 100%;
`;

export const Radio = styled.input`
  left: -9999px;
  position: absolute;

  ${({ theme }: IWithThemeProps) => large`
    &:hover, &:focus {
      & + svg [data-svg-class="ServiceTypeButton-image-fill"] {
        fill: ${theme.topSvgFillHover};
      }
    }

    &:checked {
      & + svg {
        transform: scale(1.2);
      }

      & + svg [data-svg-class="ServiceTypeButton-image-fill"] {
        fill: ${theme.topSvgFillActive};
      }
    }
  `}
`;

const svgStyles = css`
  flex: 1 0 auto;
  margin: 0 15px 0 0;

  [data-svg-class='ServiceTypeButton-image-stroke'] {
    stroke-width: 1.6;
  }

  ${({ theme }: IWithThemeProps) => large`
    flex: 0 1 auto;
    margin: 0 0 46px;
    transform: scale(1);
    transition: transform ${theme.transitionDurationPrimary} ease;

    [data-svg-class="ServiceTypeButton-image-fill"] {
      fill: ${theme.topSvgFill};
      transition: fill ${theme.transitionDurationPrimary} ease;
    }

    [data-svg-class="ServiceTypeButton-image-stroke"] {
      stroke-width: 1;
    }
  `}
`;

export const QuoteSvg = styled(SelectServiceTypeQuoteSvg)`
  ${svgStyles}

  display: none;
  height: 132px;
  width: 139px;

  ${large`
    display: block;
  `}
`;

export const QuoteSmallSvg = styled(SelectServiceTypeQuoteSmallSvg)`
  ${svgStyles}

  height: 64px;
  width: 68px;

  ${large`
    display: none;
  `}
`;

export const RepairSvg = styled(SelectServiceTypeRepairSvg)`
  ${svgStyles}

  height: 68px;
  width: 68px;

  ${large`
    height: 131px;
    width: 133px;
  `}
`;

export const MaintenanceSvg = styled(SelectServiceTypeMaintenanceSvg)`
  ${svgStyles}

  height: 86px;
  margin: 0 19px 0 8px;
  width: 56px;

  ${large`
    height: 143px;
    margin: -9px 0 0;
    width: 94px;
  `}
`;
